import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, FileSearchIcon, HammerIcon, RocketIcon, SparklesIcon, LayoutGridIcon, WebhookIcon, HeadphonesIcon, RefreshCwIcon, FactoryIcon, PackageIcon, WrenchIcon, ChevronDownIcon } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
function CheckItem({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="flex items-start gap-3" data-id="element-407">
      <div className="w-5 h-5 rounded-full bg-violet-electric/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-408">
        <CheckIcon className="w-3 h-3 text-violet-electric" data-id="element-409" />
      </div>
      <span className="text-white/70 text-sm leading-relaxed" data-id="element-410">{children}</span>
    </div>;
}
export function PricingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <>
      {/* ═══════════════════════════════════════════════════════════════
                   SECTION 1: HERO
               ═══════════════════════════════════════════════════════════════ */}
      <section id="hero" className="relative w-full pt-32 pb-24 px-6 overflow-hidden bg-navy" data-id="element-411">

        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-412">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full animate-glow-pulse" style={{
          background: 'radial-gradient(ellipse at center, rgba(108, 92, 231, 0.2) 0%, rgba(200, 80, 192, 0.08) 40%, transparent 70%)'
        }} data-id="element-413" />

        </div>

        <div className="relative z-10 max-w-3xl mx-auto text-center" data-id="element-414">
          <motion.span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} data-id="element-415">

            Pricing
          </motion.span>

          <motion.h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.08] tracking-tight mb-6" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} data-id="element-416">

            Start where it matters most. <br className="hidden sm:block" data-id="element-417" />
            <span className="bg-gradient-to-r from-violet-electric via-violet-vivid to-magenta-hot bg-clip-text text-transparent" data-id="element-418">
              Expand when you're ready.
            </span>
          </motion.h1>

          <motion.p className="text-lg text-white/55 max-w-2xl mx-auto mb-10 leading-relaxed" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.25
        }} data-id="element-419">

            Every Thruline engagement starts with a free discovery phase and a
            fixed-price project. Your team gets full platform access from day
            one — and when you're ready to expand, the next project works the
            same way.
          </motion.p>

          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.35
        }} data-id="element-420">

            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-421">

              Start Free Discovery
              <ArrowRightIcon className="w-4 h-4" data-id="element-422" />
            </a>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-navy-light to-transparent pointer-events-none" data-id="element-423" />
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                   SECTION 2: HOW IT WORKS — THREE STEPS
               ═══════════════════════════════════════════════════════════════ */}
      <section id="how-it-works" className="relative w-full bg-navy-light py-24 px-6" data-id="element-424">

        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-violet-electric/20 to-transparent" data-id="element-425" />

        <div className="max-w-5xl mx-auto" data-id="element-426">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={fadeUp} transition={{
          duration: 0.5
        }} data-id="element-427">

            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-428">
              How it works
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-id="element-429">
              Every project follows three steps.
            </h2>
            <p className="text-white/45 text-base max-w-2xl mx-auto" data-id="element-430">
              Whether it's your first module or your fourth expansion, the
              process is the same: we scope it, we build it, you run it.
            </p>
          </motion.div>

          {/* Phase Cards */}
          <motion.div className="grid grid-cols-1 lg:grid-cols-3 gap-6" variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-431">

            {/* Step 1: Discovery */}
            <motion.div variants={fadeUp} transition={{
            duration: 0.5
          }} className="relative rounded-2xl border border-white/[0.06] bg-white/[0.03] backdrop-blur-sm overflow-hidden" data-id="element-432">

              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-emerald-500" data-id="element-433" />
              <div className="p-8" data-id="element-434">
                <div className="flex items-center gap-3 mb-6" data-id="element-435">
                  <div className="w-10 h-10 rounded-xl bg-emerald-400/15 flex items-center justify-center" data-id="element-436">
                    <FileSearchIcon className="w-5 h-5 text-emerald-400" data-id="element-437" />
                  </div>
                  <div data-id="element-438">
                    <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest" data-id="element-439">
                      Step 1
                    </p>
                    <h3 className="text-lg font-semibold text-white" data-id="element-440">
                      Discovery
                    </h3>
                  </div>
                </div>

                <div className="inline-flex items-baseline gap-1 mb-6" data-id="element-441">
                  <span className="text-3xl font-bold text-emerald-400" data-id="element-442">
                    Free
                  </span>
                </div>

                <p className="text-white/50 text-sm leading-relaxed mb-6" data-id="element-443">
                  You share your operational documents — SOPs, spreadsheets,
                  emails, whatever captures how your team actually works.
                  Thruline's system ingests them and reconstructs your
                  workflows. A consultant reviews the output, identifies gaps,
                  and builds a scoped project plan.
                </p>

                <div className="space-y-3" data-id="element-444">
                  <h4 className="text-white/30 text-[10px] font-bold uppercase tracking-widest" data-id="element-445">
                    What you walk away with
                  </h4>
                  <CheckItem data-id="element-446">
                    A requirements map built from your own documents
                  </CheckItem>
                  <CheckItem data-id="element-447">
                    Consultant's assessment: what works, what's broken, what the
                    system should do
                  </CheckItem>
                  <CheckItem data-id="element-448">
                    A scoped project plan with a fixed-price quote
                  </CheckItem>
                  <CheckItem data-id="element-449">
                    No cost. No obligation. Walk away if it's not right.
                  </CheckItem>
                </div>
              </div>
            </motion.div>

            {/* Step 2: Implementation */}
            <motion.div variants={fadeUp} transition={{
            duration: 0.5
          }} className="relative rounded-2xl border border-violet-electric/20 bg-white/[0.05] backdrop-blur-sm overflow-hidden ring-1 ring-violet-electric/10" data-id="element-450">

              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-electric to-violet-vivid" data-id="element-451" />
              <div className="p-8" data-id="element-452">
                <div className="flex items-center gap-3 mb-6" data-id="element-453">
                  <div className="w-10 h-10 rounded-xl bg-violet-electric/15 flex items-center justify-center" data-id="element-454">
                    <HammerIcon className="w-5 h-5 text-violet-electric" data-id="element-455" />
                  </div>
                  <div data-id="element-456">
                    <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest" data-id="element-457">
                      Step 2
                    </p>
                    <h3 className="text-lg font-semibold text-white" data-id="element-458">
                      Implementation
                    </h3>
                  </div>
                </div>

                <div className="inline-flex items-baseline gap-1 mb-6" data-id="element-459">
                  <span className="text-3xl font-bold text-white" data-id="element-460">
                    Fixed price
                  </span>
                </div>

                <p className="text-white/50 text-sm leading-relaxed mb-6" data-id="element-461">
                  You approve the project plan, and Thruline builds your system.
                  Every configuration, workflow, and integration is traced back
                  to a specific requirement from your documents. A consultant
                  verifies the output. Documentation and training materials are
                  generated automatically.
                </p>

                <div className="space-y-3" data-id="element-462">
                  <h4 className="text-white/30 text-[10px] font-bold uppercase tracking-widest" data-id="element-463">
                    What you walk away with
                  </h4>
                  <CheckItem data-id="element-464">
                    A fully configured project — the module(s) you scoped, ready
                    to operate
                  </CheckItem>
                  <CheckItem data-id="element-465">
                    Every configuration traced to a documented requirement
                  </CheckItem>
                  <CheckItem data-id="element-466">
                    Auto-generated documentation & training materials
                  </CheckItem>
                  <CheckItem data-id="element-467">
                    Consultant-verified quality, not just automated output
                  </CheckItem>
                  <CheckItem data-id="element-468">
                    Data migration from your existing systems
                  </CheckItem>
                </div>
              </div>
            </motion.div>

            {/* Step 3: Operate */}
            <motion.div variants={fadeUp} transition={{
            duration: 0.5
          }} className="relative rounded-2xl border border-white/[0.06] bg-white/[0.03] backdrop-blur-sm overflow-hidden" data-id="element-469">

              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-magenta-hot to-pink-400" data-id="element-470" />
              <div className="p-8" data-id="element-471">
                <div className="flex items-center gap-3 mb-6" data-id="element-472">
                  <div className="w-10 h-10 rounded-xl bg-magenta-hot/15 flex items-center justify-center" data-id="element-473">
                    <RocketIcon className="w-5 h-5 text-magenta-hot" data-id="element-474" />
                  </div>
                  <div data-id="element-475">
                    <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest" data-id="element-476">
                      Step 3
                    </p>
                    <h3 className="text-lg font-semibold text-white" data-id="element-477">
                      Operate
                    </h3>
                  </div>
                </div>

                <div className="inline-flex items-baseline gap-2 mb-6" data-id="element-478">
                  <span className="text-3xl font-bold text-white" data-id="element-479">
                    Per user
                  </span>
                  <span className="text-white/40 text-sm" data-id="element-480">/month</span>
                </div>

                <p className="text-white/50 text-sm leading-relaxed mb-6" data-id="element-481">
                  Your project goes live. Every licensed user gets access to the
                  full Thruline platform — not just the module you implemented,
                  but everything. Reports, dashboards, workflows, document
                  templates, all modules. No feature gating, no module-by-module
                  upsells.
                </p>

                <div className="space-y-3" data-id="element-482">
                  <h4 className="text-white/30 text-[10px] font-bold uppercase tracking-widest" data-id="element-483">
                    What you walk away with
                  </h4>
                  <CheckItem data-id="element-484">
                    Full platform access for every licensed user
                  </CheckItem>
                  <CheckItem data-id="element-485">
                    All modules included, not just the one you implemented first
                  </CheckItem>
                  <CheckItem data-id="element-486">
                    Unlimited workflows, reports, dashboards & document
                    templates
                  </CheckItem>
                  <CheckItem data-id="element-487">
                    Ongoing support from the team that built your system
                  </CheckItem>
                  <CheckItem data-id="element-488">
                    A self-documenting ERP that trains your team as they use it
                  </CheckItem>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Repeatable cycle indicator */}
          <motion.div className="flex items-center justify-center mt-10 gap-3" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} transition={{
          duration: 0.5,
          delay: 0.2
        }} data-id="element-489">

            <RefreshCwIcon className="w-4 h-4 text-violet-electric/50" data-id="element-490" />
            <p className="text-white/30 text-sm" data-id="element-491">
              Same process for every expansion. Discovery is always free.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                   SECTION 3: WHEN YOU'RE READY TO GROW
               ═══════════════════════════════════════════════════════════════ */}
      <section id="expansion" className="relative w-full bg-navy py-24 px-6" data-id="element-492">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" data-id="element-493" />

        <div className="max-w-5xl mx-auto" data-id="element-494">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={fadeUp} transition={{
          duration: 0.5
        }} data-id="element-495">

            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-496">
              Expand on your terms
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-id="element-497">
              Your next project works the same way.
            </h2>
            <p className="text-white/45 text-base max-w-2xl mx-auto" data-id="element-498">
              You don't have to buy your entire ERP on day one. Start with the
              area that hurts the most, get it running, then expand when the
              business is ready.
            </p>
          </motion.div>

          {/* Narrative blocks */}
          <motion.div className="max-w-3xl mx-auto space-y-6 mb-16" variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-499">

            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} data-id="element-500">

              <h3 className="text-white font-semibold text-base mb-2" data-id="element-501">
                Start with what matters most.
              </h3>
              <p className="text-white/45 text-sm leading-relaxed" data-id="element-502">
                Most companies don't need their entire ERP on day one. You start
                with the area that hurts the most — maybe it's production, maybe
                it's inventory, maybe it's purchasing. You get that running.
                Your team learns the system in a real operational context. Then,
                when the next pain point surfaces or the business grows into a
                new area, you scope another project.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} data-id="element-503">

              <h3 className="text-white font-semibold text-base mb-2" data-id="element-504">
                Each expansion follows the same three steps.
              </h3>
              <p className="text-white/45 text-sm leading-relaxed" data-id="element-505">
                Free discovery, fixed-price project, same license. Your
                documents go in, a scoped plan comes out, you approve the price
                before work begins. Because your first project is already live,
                the system has context — the new project builds on top of what's
                already there, with the same traceability, the same
                documentation, and the same support team.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} data-id="element-506">

              <h3 className="text-white font-semibold text-base mb-2" data-id="element-507">
                You're never locked in, and you're never locked out.
              </h3>
              <p className="text-white/45 text-sm leading-relaxed" data-id="element-508">
                There's no penalty for starting small and no gate preventing you
                from expanding. Your license already includes every module. The
                only cost for an expansion is the project itself — scoped to
                exactly what you need, priced before you commit.
              </p>
            </motion.div>
          </motion.div>

          {/* Example expansion journey */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={fadeUp} transition={{
          duration: 0.5,
          delay: 0.1
        }} data-id="element-509">

            <h3 className="text-white/30 text-[10px] font-bold uppercase tracking-widest text-center mb-8" data-id="element-510">
              Example expansion journey
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-511">
              {/* Project 1 */}
              <div className="relative rounded-2xl border border-white/[0.06] bg-white/[0.03] p-6 overflow-hidden" data-id="element-512">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-electric to-violet-vivid" data-id="element-513" />
                <div className="flex items-center gap-3 mb-4" data-id="element-514">
                  <div className="w-8 h-8 rounded-lg bg-violet-electric/15 flex items-center justify-center" data-id="element-515">
                    <FactoryIcon className="w-4 h-4 text-violet-electric" data-id="element-516" />
                  </div>
                  <div data-id="element-517">
                    <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest" data-id="element-518">
                      Project 1
                    </p>
                    <p className="text-white text-sm font-semibold" data-id="element-519">Day one</p>
                  </div>
                </div>
                <h4 className="text-white font-semibold text-base mb-2" data-id="element-520">
                  Production + BoMs
                </h4>
                <p className="text-white/40 text-xs mb-4" data-id="element-521">
                  Production tracking is manual and error-prone
                </p>
                <div className="space-y-2 text-xs" data-id="element-522">
                  <div className="flex justify-between" data-id="element-523">
                    <span className="text-white/30" data-id="element-524">Discovery</span>
                    <span className="text-emerald-400 font-medium" data-id="element-525">Free</span>
                  </div>
                  <div className="flex justify-between" data-id="element-526">
                    <span className="text-white/30" data-id="element-527">Implementation</span>
                    <span className="text-white/60 font-medium" data-id="element-528">
                      Fixed-price project
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-529">
                    <span className="text-white/30" data-id="element-530">License</span>
                    <span className="text-white/60 font-medium" data-id="element-531">
                      Per user / month
                    </span>
                  </div>
                </div>
              </div>

              {/* Project 2 */}
              <div className="relative rounded-2xl border border-white/[0.06] bg-white/[0.03] p-6 overflow-hidden" data-id="element-532">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-vivid to-magenta-hot" data-id="element-533" />
                <div className="flex items-center gap-3 mb-4" data-id="element-534">
                  <div className="w-8 h-8 rounded-lg bg-magenta-hot/15 flex items-center justify-center" data-id="element-535">
                    <PackageIcon className="w-4 h-4 text-magenta-hot" data-id="element-536" />
                  </div>
                  <div data-id="element-537">
                    <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest" data-id="element-538">
                      Project 2
                    </p>
                    <p className="text-white text-sm font-semibold" data-id="element-539">
                      6 months later
                    </p>
                  </div>
                </div>
                <h4 className="text-white font-semibold text-base mb-2" data-id="element-540">
                  Inventory + Purchasing
                </h4>
                <p className="text-white/40 text-xs mb-4" data-id="element-541">
                  Stockouts and over-ordering are costing money
                </p>
                <div className="space-y-2 text-xs" data-id="element-542">
                  <div className="flex justify-between" data-id="element-543">
                    <span className="text-white/30" data-id="element-544">Discovery</span>
                    <span className="text-emerald-400 font-medium" data-id="element-545">Free</span>
                  </div>
                  <div className="flex justify-between" data-id="element-546">
                    <span className="text-white/30" data-id="element-547">Implementation</span>
                    <span className="text-white/60 font-medium" data-id="element-548">
                      Fixed-price project
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-549">
                    <span className="text-white/30" data-id="element-550">License</span>
                    <span className="text-white/60 font-medium" data-id="element-551">
                      Per user / month (unchanged)
                    </span>
                  </div>
                </div>
              </div>

              {/* Project 3 */}
              <div className="relative rounded-2xl border border-white/[0.06] bg-white/[0.03] p-6 overflow-hidden" data-id="element-552">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-magenta-hot to-pink-400" data-id="element-553" />
                <div className="flex items-center gap-3 mb-4" data-id="element-554">
                  <div className="w-8 h-8 rounded-lg bg-pink-400/15 flex items-center justify-center" data-id="element-555">
                    <WrenchIcon className="w-4 h-4 text-pink-400" data-id="element-556" />
                  </div>
                  <div data-id="element-557">
                    <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest" data-id="element-558">
                      Project 3
                    </p>
                    <p className="text-white text-sm font-semibold" data-id="element-559">Year two</p>
                  </div>
                </div>
                <h4 className="text-white font-semibold text-base mb-2" data-id="element-560">
                  Field Operations
                </h4>
                <p className="text-white/40 text-xs mb-4" data-id="element-561">
                  New service division launched
                </p>
                <div className="space-y-2 text-xs" data-id="element-562">
                  <div className="flex justify-between" data-id="element-563">
                    <span className="text-white/30" data-id="element-564">Discovery</span>
                    <span className="text-emerald-400 font-medium" data-id="element-565">Free</span>
                  </div>
                  <div className="flex justify-between" data-id="element-566">
                    <span className="text-white/30" data-id="element-567">Implementation</span>
                    <span className="text-white/60 font-medium" data-id="element-568">
                      Fixed-price project
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-569">
                    <span className="text-white/30" data-id="element-570">License</span>
                    <span className="text-white/60 font-medium" data-id="element-571">
                      New users added
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                   SECTION 4: WHAT EVERY LICENSE INCLUDES
               ═══════════════════════════════════════════════════════════════ */}
      <section id="platform-access" className="relative w-full bg-navy-light py-24 px-6" data-id="element-572">

        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-violet-electric/20 to-transparent" data-id="element-573" />

        <div className="max-w-5xl mx-auto" data-id="element-574">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={fadeUp} transition={{
          duration: 0.5
        }} data-id="element-575">

            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-576">
              Full platform access
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-id="element-577">
              One license. The full platform.
            </h2>
            <p className="text-white/45 text-base max-w-xl mx-auto" data-id="element-578">
              Every user gets everything. No tiers. No upgrades. No modules to
              unlock.
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-8" variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-579">

            {/* Platform */}
            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-580">

              <div className="flex items-center gap-3 mb-5" data-id="element-581">
                <LayoutGridIcon className="w-5 h-5 text-violet-electric" data-id="element-582" />
                <h3 className="text-white font-semibold text-sm tracking-wide" data-id="element-583">
                  Platform
                </h3>
              </div>
              <div className="space-y-3" data-id="element-584">
                <CheckItem data-id="element-585">
                  All Thruline modules (Production, Inventory, Purchasing, BoMs,
                  and more)
                </CheckItem>
                <CheckItem data-id="element-586">
                  Unlimited triggered and scheduled workflows
                </CheckItem>
                <CheckItem data-id="element-587">Unlimited reports and dashboards</CheckItem>
                <CheckItem data-id="element-588">Unlimited document templates</CheckItem>
                <CheckItem data-id="element-589">Full mobile access</CheckItem>
              </div>
            </motion.div>

            {/* With every project */}
            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-590">

              <div className="flex items-center gap-3 mb-5" data-id="element-591">
                <SparklesIcon className="w-5 h-5 text-violet-electric" data-id="element-592" />
                <h3 className="text-white font-semibold text-sm tracking-wide" data-id="element-593">
                  With every project
                </h3>
              </div>
              <div className="space-y-3" data-id="element-594">
                <CheckItem data-id="element-595">
                  AI-powered document ingestion and requirements construction
                </CheckItem>
                <CheckItem data-id="element-596">Expert consultant review and gap analysis</CheckItem>
                <CheckItem data-id="element-597">
                  Traced configuration (every setting linked to a requirement)
                </CheckItem>
                <CheckItem data-id="element-598">
                  Auto-generated documentation and training materials
                </CheckItem>
                <CheckItem data-id="element-599">Data migration</CheckItem>
              </div>
            </motion.div>

            {/* Integrations */}
            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-600">

              <div className="flex items-center gap-3 mb-5" data-id="element-601">
                <WebhookIcon className="w-5 h-5 text-violet-electric" data-id="element-602" />
                <h3 className="text-white font-semibold text-sm tracking-wide" data-id="element-603">
                  Integrations
                </h3>
              </div>
              <div className="space-y-3" data-id="element-604">
                <CheckItem data-id="element-605">
                  Pre-built integrations (QuickBooks, Shopify, Slack, Stripe,
                  etc.)
                </CheckItem>
                <CheckItem data-id="element-606">Custom inbound webhooks</CheckItem>
                <CheckItem data-id="element-607">Single Sign-On (SSO)</CheckItem>
                <CheckItem data-id="element-608">
                  Custom integrations (scoped during implementation)
                </CheckItem>
                <CheckItem data-id="element-609">
                  GovCloud available for government contractors
                </CheckItem>
              </div>
            </motion.div>

            {/* Support */}
            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-610">

              <div className="flex items-center gap-3 mb-5" data-id="element-611">
                <HeadphonesIcon className="w-5 h-5 text-violet-electric" data-id="element-612" />
                <h3 className="text-white font-semibold text-sm tracking-wide" data-id="element-613">
                  Support
                </h3>
              </div>
              <div className="space-y-3" data-id="element-614">
                <CheckItem data-id="element-615">
                  Ongoing support from the team that built your system
                </CheckItem>
                <CheckItem data-id="element-616">Workflow development assistance</CheckItem>
                <CheckItem data-id="element-617">
                  System evolution as your operations change
                </CheckItem>
                <CheckItem data-id="element-618">
                  Self-documenting ERP — always-current training materials
                </CheckItem>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                   SECTION 5: WHY THIS MODEL WORKS
               ═══════════════════════════════════════════════════════════════ */}
      <section id="philosophy" className="relative w-full bg-navy py-24 px-6" data-id="element-619">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" data-id="element-620" />

        <div className="max-w-3xl mx-auto" data-id="element-621">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={fadeUp} transition={{
          duration: 0.5
        }} data-id="element-622">

            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-623">
              Our philosophy
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-624">
              The logic behind the pricing.
            </h2>
          </motion.div>

          <motion.div className="space-y-4" variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-625">

            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-626">

              <h3 className="text-white font-semibold text-base mb-3" data-id="element-627">
                "Why is discovery free?"
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-628">
                Because we're not interviewing your team for six weeks. Thruline
                reads your documents and reconstructs your workflows in hours,
                not months. The discovery phase produces a real, scoped project
                plan with a fixed-price quote. If it doesn't make sense for you,
                you've lost nothing. We can do this because the Thruline ERP
                Factory makes discovery dramatically cheaper than traditional
                consulting — so we absorb the cost.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-629">

              <h3 className="text-white font-semibold text-base mb-3" data-id="element-630">
                "Why fixed-price projects instead of hourly billing?"
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-631">
                Because hourly billing is how ERP implementations balloon. The
                consultant's meter starts running in the first discovery meeting
                and doesn't stop until months after go-live. Thruline scopes
                each project during discovery and quotes a number you approve
                before work begins. The Thruline ERP Factory automates the
                production work and focuses consultant time on expert review.
                The result: predictable cost, no overruns, and you know exactly
                what you're paying for.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-632">

              <h3 className="text-white font-semibold text-base mb-3" data-id="element-633">
                "Why pay per project instead of one big upfront fee?"
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-634">
                Because most companies don't need their entire ERP on day one,
                and paying for everything upfront means paying for modules you
                won't use for months or years. The per-project model lets you
                start where the pain is, prove value with your team, and expand
                when the business is ready — not when a sales contract forces
                you to. Each project is a self-contained engagement with its own
                scope, its own price, and its own deliverable. You're never
                overpaying for capabilities you haven't activated.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} transition={{
            duration: 0.45
          }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-635">

              <h3 className="text-white font-semibold text-base mb-3" data-id="element-636">
                "Why one license tier instead of Starter, Pro, and Enterprise?"
              </h3>
              <p className="text-white/50 text-sm leading-relaxed" data-id="element-637">
                Because feature gating is how legacy ERP vendors extract revenue
                after the sale. You buy the base tier, realize you need
                something locked behind the next one, and upgrade under
                pressure. Thruline includes everything in every license. The
                project determines which modules your team uses first; the
                license gives them access to all of them. When you're ready to
                activate a new module, you run a project — not an upgrade
                purchase.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                   SECTION 6: COMPARISON TABLE
               ═══════════════════════════════════════════════════════════════ */}
      <section id="comparison" className="relative w-full bg-navy-light py-24 px-6" data-id="element-638">

        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-violet-electric/20 to-transparent" data-id="element-639" />

        <div className="max-w-4xl mx-auto" data-id="element-640">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={fadeUp} transition={{
          duration: 0.5
        }} data-id="element-641">

            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-642">
              Comparison
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-643">
              How the old model compares.
            </h2>
          </motion.div>

          <motion.div className="rounded-2xl border border-white/[0.06] overflow-hidden" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={fadeUp} transition={{
          duration: 0.5,
          delay: 0.1
        }} data-id="element-644">

            {/* Header */}
            <div className="grid grid-cols-3 border-b border-white/[0.06]" data-id="element-645">
              <div className="p-5 bg-white/[0.01]" data-id="element-646" />
              <div className="p-5 bg-white/[0.02] text-center border-l border-white/[0.06]" data-id="element-647">
                <span className="text-white/40 text-xs font-bold uppercase tracking-widest" data-id="element-648">
                  Traditional ERP
                </span>
              </div>
              <div className="p-5 bg-violet-electric/[0.08] text-center border-l border-violet-electric/20" data-id="element-649">
                <span className="text-violet-electric text-xs font-bold uppercase tracking-widest" data-id="element-650">
                  Thruline
                </span>
              </div>
            </div>

            {/* Rows */}
            {[['Discovery cost', 'Paid from meeting one', 'Free'], ['Pricing model', 'Hourly, open-ended', 'Fixed price per project, scoped upfront'], ['Scope clarity', 'Emerges over months', 'Defined before you commit'], ['Entry commitment', 'Buy the whole system upfront', 'Start with one project, expand later'], ['Feature access', 'Tiered / per-module licensing', 'Everything included in every license'], ['Timeline', '3–9 months', 'Weeks per project'], ['Documentation', 'Goes stale before go-live', 'Auto-generated, traced, always current'], ['Support model', 'Generic helpdesk', 'The team that built your system'], ['Traceability', 'None', 'Every config traced to a requirement'], ['Expansion cost', 'Module upgrade fees + re-implementation', 'New project, same process, fixed price']].map(([label, traditional, thruline], i) => <div key={label} className={`grid grid-cols-3 ${i < 9 ? 'border-b border-white/[0.04]' : ''}`} data-id="element-651">

                <div className="p-4 sm:p-5 bg-white/[0.01]" data-id="element-652">
                  <span className="text-white/80 text-sm font-medium" data-id="element-653">
                    {label}
                  </span>
                </div>
                <div className="p-4 sm:p-5 bg-white/[0.02] border-l border-white/[0.06]" data-id="element-654">
                  <span className="text-white/35 text-sm" data-id="element-655">{traditional}</span>
                </div>
                <div className="p-4 sm:p-5 bg-violet-electric/[0.04] border-l border-violet-electric/10" data-id="element-656">
                  <span className="text-white/70 text-sm font-medium" data-id="element-657">
                    {thruline}
                  </span>
                </div>
              </div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
                   SECTION 7: BOTTOM CTA
               ═══════════════════════════════════════════════════════════════ */}
      <section id="cta" className="relative w-full py-24 px-6 overflow-hidden" data-id="element-658">
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(135deg, #0D0B2E 0%, #1A1452 30%, #6C5CE7 70%, #C850C0 100%)'
      }} data-id="element-659" />

        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")"
      }} data-id="element-660" />


        <motion.div className="relative z-10 max-w-3xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={fadeUp} transition={{
        duration: 0.6
      }} data-id="element-661">

          <span className="text-white/60 text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-662">
            Get started
          </span>
          <h2 className="text-3xl sm:text-5xl font-bold text-white tracking-tight mb-6" data-id="element-663">
            Start with what matters most.
            <br className="hidden sm:block" data-id="element-664" />
            Discovery is free.
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto" data-id="element-665">
            Send us your documents. We'll send you a project plan and a
            fixed-price quote. No cost, no commitment.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-666">
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-667">

              Start Free Discovery
              <ArrowRightIcon className="w-4 h-4" data-id="element-668" />
            </a>
            <a href="/resources/thesis" className="inline-flex items-center px-8 py-3.5 rounded-full border border-white/30 text-white font-medium text-sm hover:border-white/60 transition-all" data-id="element-669">

              Read the ERP Factory Thesis
            </a>
          </div>
        </motion.div>
      </section>
    </>;
}